import { faCircleHalfStroke } from "@fortawesome/pro-regular-svg-icons/faCircleHalfStroke";
import { faShuffle } from "@fortawesome/pro-regular-svg-icons/faShuffle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, FormHelperText, Grid, IconButton, RadioGroup, Stack, TextField, useTheme } from "@mui/material";
import { FIELD_NUMBER_MAXVALUE, FIELD_NUMBER_MINVALUE } from "app/appConstants";
import { MprcButton, MprcChipInput, MprcFormSubHeader, MprcIconButtonMoreOptionsDropdown, MprcStringToHtml, MprcText } from "components";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcNumberField } from "components/MprcNumberField";
import { MprcRadio } from "components/MprcRadio";
import { ManagedQuestionAlternativeDto, ManagedQuestionResponse, QuestionType } from "generated/CoreApi_v1";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from 'uuid';
import { MAX_ALTERNATIVES } from "views/assessment/common/assessment";
import { PointTypedButton } from "views/assessment/questionBank/components/QuestionCommon";
import { useQuestionContext } from "views/assessment/questionBank/Home/EditQuestion/EditQuestion";

////////////////////////////////////////////////////////////////////////////////

export interface IPostValidationFillIn {
    alternatives: string[] | undefined;
    fillInMinValue: string[] | undefined;
    fillInMaxValue: string[] | undefined;
}

export interface IValidationFieldsFillIn {
    alternatives: boolean;
    fillInMinValue: boolean;
    fillInMaxValue: boolean;
}

///
export function validateFillIn(dto: ManagedQuestionResponse, forSurvey: boolean): IValidationFieldsFillIn {
    let alternatives = !dto.alternatives.some(x => x.text == undefined || x.text.length == 0)
        && (!dto.alternatives.some(x => x.evaluation?.correctAnswer == undefined || x.evaluation?.correctAnswer.length == 0) || forSurvey);

    if (!forSurvey && alternatives && dto.questionType == QuestionType.FillInNumber) {
        alternatives = !dto.alternatives.some(x => parseFloat(x.evaluation!.correctAnswer) < FIELD_NUMBER_MINVALUE || parseFloat(x.evaluation!.correctAnswer) > FIELD_NUMBER_MAXVALUE);
    }

    return {
        alternatives,
        fillInMinValue: !forSurvey || (dto.survey?.fillInMinValue != undefined
            && dto.survey?.fillInMinValue >= FIELD_NUMBER_MINVALUE && dto.survey?.fillInMinValue <= FIELD_NUMBER_MAXVALUE),
        fillInMaxValue: !forSurvey || (dto.survey?.fillInMaxValue != undefined
            && dto.survey?.fillInMaxValue >= FIELD_NUMBER_MINVALUE && dto.survey?.fillInMaxValue <= FIELD_NUMBER_MAXVALUE
            && (dto.survey?.fillInMaxValue ?? 0) >= (dto.survey?.fillInMinValue ?? 0)),
    };
}

type QuestionAlternativeContextMenuOptions = "moveUp" | "moveDown" | "remove";

interface IProps {
    canSelectType: boolean;
}

/////////////////////////////////////////////////////////////////////////////////

export const EditQuestionFillIn: React.FC<IProps> = (props) => {

    const { canSelectType } = props;

    const { viewData, postValidation, validationFields, forSurvey, readonly, handleUpdateRecord, handleUpdateEvaluation, handleUpdateSurvey } = useQuestionContext();

    const currentPostValidation = postValidation.fillInBox;
    const currentValidationFields = validationFields.fillInBox;

    const { t } = useTranslation("assessment");
    const theme = useTheme();

    ///
    const handleAddAlternative = () => {
        var newAlternativeId = uuidv4();
        if (viewData.alternatives.length < MAX_ALTERNATIVES) {
            handleUpdateRecord("alternatives",
                [
                    ...viewData.alternatives,
                    { alternativeId: newAlternativeId, text: "", displayOrder: 1, evaluation: { feedback: null, correctAnswer: "", answerIsNumber: false }, survey: { points: null } },
                ]
            );

            setTimeout(() => {
                const el = document.getElementById(newAlternativeId);
                if (el) el.focus();
            }, 300);
        }
    };

    ///
    const handleSelectAlternativeOption = (option: QuestionAlternativeContextMenuOptions, alternative: ManagedQuestionAlternativeDto, index: number) => {
        switch (option) {
            case "remove":
                removeAlternative(alternative.alternativeId);
                break;

            case "moveUp":
                moveAlternative(index, "up");
                break;

            case "moveDown":
                moveAlternative(index, "down");
                break;
        }
    };

    ///
    const removeAlternative = (id: string) => {
        var newAlternatives = viewData.alternatives.filter(x => x.alternativeId != id);
        handleUpdateRecord("alternatives", newAlternatives);
    };

    ///
    const moveAlternative = (index: number, moveTo: "up" | "down") => {
        var newAlternatives = viewData.alternatives.map(x => x);
        var element = newAlternatives[index];
        newAlternatives.splice(index, 1);
        newAlternatives.splice(index + (moveTo == "up" ? -1 : 1), 0, element);
        handleUpdateRecord("alternatives", newAlternatives);
    };

    ///
    const getQuestionAlternativeMenuItems = useCallback(
        (item: ManagedQuestionAlternativeDto, index: number): MprcDropdownItems<QuestionAlternativeContextMenuOptions> => {

            let group0: MprcDropdownItems<QuestionAlternativeContextMenuOptions> = [];
            if (index != 0)
                group0.push({ text: t("Question.Edit.Alternative.MoveUpAction"), value: "moveUp" });
            if (index != viewData.alternatives.length - 1)
                group0.push({ text: t("Question.Edit.Alternative.MoveDownAction"), value: "moveDown" });

            let group1: MprcDropdownItems<QuestionAlternativeContextMenuOptions> = [];
            if (viewData.alternatives.length > 1 && viewData.changeVersion == true) {
                group1.push({ text: <MprcText color="error">{t("Actions.Delete", { ns: "shared" })}</MprcText>, value: "remove" });
            }

            return MprcDropdownBuildGroupedItems<QuestionAlternativeContextMenuOptions>([group0, group1]);
        }
        , [viewData.alternatives, viewData.changeVersion]
    );

    ///
    const RenderButtonsEvaluation = () => {
        return (
            <Stack direction="row" spacing={1}>
                <IconButton
                    title={t("Question.Edit.FillIn.AllowPartialScoreTooltip")}
                    onClick={() => handleUpdateEvaluation("allowPartialScore", !viewData.evaluation?.allowPartialScore)}
                    disabled={readonly}>
                    <FontAwesomeIcon
                        icon={faCircleHalfStroke}
                        color={viewData.evaluation?.allowPartialScore ? theme.palette.highlight.main : theme.palette.muted.main}
                        fixedWidth
                    />
                </IconButton>
                <IconButton
                    title={t("Question.Edit.FillIn.RandomizeTooltip")}
                    onClick={() => handleUpdateEvaluation("randomized", !viewData.evaluation?.randomized)}
                    disabled={readonly}>
                    <FontAwesomeIcon
                        icon={faShuffle}
                        color={viewData.evaluation?.randomized ? theme.palette.highlight.main : theme.palette.muted.main}
                        fixedWidth
                    />
                </IconButton>
            </Stack>
        );
    };

    ///
    const RenderButtonsSurvey = () => {
        return (
            <Stack direction="row" spacing={1}>
                <PointTypedButton
                    tooltip={t("Question.Edit.FillIn.PointTypedTooltip")}
                    active={viewData.survey?.pointTyped ?? false}
                    readonly={readonly}
                    onClick={() => handleUpdateSurvey("pointTyped", !viewData.survey?.pointTyped)}
                />
            </Stack>
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <Grid container mb={1}>
                <Grid size="grow">
                    {
                        forSurvey
                            ?
                            <>
                                <Grid container spacing={2}>
                                    <Grid>
                                        <MprcNumberField
                                            value={viewData.survey?.fillInMinValue ?? 0}
                                            label={t("Question.Edit.FillIn.FillInMinValueLabel")}
                                            allowNegative
                                            min={FIELD_NUMBER_MINVALUE}
                                            max={FIELD_NUMBER_MAXVALUE}
                                            sx={{ width: 120 }}
                                            onChange={value => handleUpdateSurvey("fillInMinValue", value)}
                                            error={!currentValidationFields.fillInMinValue || !!currentPostValidation.fillInMinValue}
                                        />
                                    </Grid>
                                    <Grid>
                                        <MprcNumberField
                                            value={viewData.survey?.fillInMaxValue ?? 0}
                                            label={t("Question.Edit.FillIn.FillInMaxValueLabel")}
                                            allowNegative
                                            min={FIELD_NUMBER_MINVALUE}
                                            max={FIELD_NUMBER_MAXVALUE}
                                            sx={{ width: 120 }}
                                            onChange={value => handleUpdateSurvey("fillInMaxValue", value)}
                                            error={!currentValidationFields.fillInMaxValue || !!currentPostValidation.fillInMaxValue}
                                        />
                                    </Grid>
                                </Grid>
                                {
                                    !!currentPostValidation.fillInMinValue &&
                                    <FormHelperText error>{<MprcStringToHtml text={currentPostValidation?.fillInMinValue} />}</FormHelperText>
                                }
                                {
                                    !!currentPostValidation.fillInMaxValue &&
                                    <FormHelperText error>{<MprcStringToHtml text={currentPostValidation?.fillInMaxValue} />}</FormHelperText>
                                }
                            </>
                            :
                            <RadioGroup
                                value={viewData.questionType}
                                onChange={e => handleUpdateRecord("questionType", Number(e.target.value) as QuestionType)}>
                                <Grid container>
                                    <Grid>
                                        <MprcRadio
                                            value={QuestionType.Dropdown}
                                            label={t("Question.Edit.FillIn.DropdownOption")}
                                            disabled={readonly || !canSelectType}
                                        />
                                    </Grid>
                                    <Grid>
                                        <MprcRadio
                                            value={QuestionType.FillInText}
                                            label={t("Question.Edit.FillIn.FillInTextOption")}
                                            disabled={readonly || !canSelectType}
                                        />
                                    </Grid>
                                    <Grid>
                                        <MprcRadio
                                            value={QuestionType.FillInNumber}
                                            label={t("Question.Edit.FillIn.FillInNumberOption")}
                                            disabled={readonly || !canSelectType}
                                        />
                                    </Grid>
                                </Grid>
                            </RadioGroup>
                    }
                </Grid>
                <Grid>
                    {
                        forSurvey
                            ? RenderButtonsSurvey()
                            : RenderButtonsEvaluation()
                    }
                </Grid>
            </Grid>
            {
                viewData.alternatives.map((v, index) => {
                    return (
                        <Grid key={v.alternativeId} container alignContent="center" alignItems="flex-start" spacing={1} mt={0.5}>
                            <Grid size="grow">
                                <TextField
                                    id={v.alternativeId}
                                    fullWidth
                                    placeholder={t("Question.Edit.FillIn.TextPlaceholder")}
                                    value={viewData.alternatives[index].text}
                                    onChange={(e) => {
                                        var newAlternatives = [...viewData.alternatives];
                                        newAlternatives[index].text = e.target.value;
                                        handleUpdateRecord("alternatives", newAlternatives);
                                    }}
                                    disabled={readonly}
                                    slotProps={{
                                        input: {
                                            inputProps: { maxLength: 512 }
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key == 'Enter') {
                                            if (index < viewData.alternatives.length - 1) {
                                                setTimeout(() => {
                                                    const el = document.getElementById(viewData.alternatives[index + 1].alternativeId);
                                                    if (el) el.focus();
                                                }, 100);
                                            }
                                            else if (viewData.alternatives[index].text.length > 0 && viewData.alternatives.length < MAX_ALTERNATIVES) {
                                                handleAddAlternative();
                                            }
                                        }
                                    }}
                                />
                            </Grid>
                            {
                                !forSurvey &&
                                <>
                                    {
                                        viewData.questionType == QuestionType.Dropdown || viewData.questionType == QuestionType.FillInText
                                            ?
                                            <Grid size={{ xs: 4 }}>
                                                <TextField
                                                    id={v.evaluation?.correctAnswer}
                                                    fullWidth
                                                    placeholder={t("Question.Edit.FillIn.CorrectAnswerPlaceholder")}
                                                    value={viewData.alternatives[index].evaluation!.correctAnswer}
                                                    onChange={(e) => {
                                                        var newAlternatives = [...viewData.alternatives];
                                                        newAlternatives[index].evaluation!.correctAnswer = e.target.value;
                                                        handleUpdateRecord("alternatives", newAlternatives);
                                                    }}
                                                    disabled={readonly}
                                                    slotProps={{
                                                        input: {
                                                            inputProps: { maxLength: 512 }
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key == 'Enter') {
                                                            if (index < viewData.alternatives.length - 1) {
                                                                const el = document.getElementById(viewData.alternatives[index + 1].alternativeId);
                                                                if (el) el.focus();
                                                            }
                                                            else if (viewData.alternatives[index].text.length > 0 && viewData.alternatives.length < MAX_ALTERNATIVES) {
                                                                handleAddAlternative();
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            :
                                            <Grid size={{ xs: 3 }}>
                                                <MprcNumberField
                                                    allowNegative
                                                    value={viewData.alternatives[index].evaluation!.correctAnswer.length > 0 && !isNaN(Number(viewData.alternatives[index].evaluation!.correctAnswer)) ? Number(viewData.alternatives[index].evaluation!.correctAnswer) : null}
                                                    fullWidth
                                                    decimals={2}
                                                    min={FIELD_NUMBER_MINVALUE}
                                                    max={FIELD_NUMBER_MAXVALUE}
                                                    onChange={value => {
                                                        var newAlternatives = [...viewData.alternatives];
                                                        newAlternatives[index].evaluation!.correctAnswer = value?.toString() ?? "";
                                                        handleUpdateRecord("alternatives", newAlternatives);
                                                    }}
                                                    slotProps={{ input: { endAdornment: t("Question.Edit.FillIn.CorrectNumberAnswerPlaceholder") } }}
                                                />
                                            </Grid>
                                    }
                                </>
                            }
                            <Grid>
                                {
                                    readonly != true &&
                                    <MprcIconButtonMoreOptionsDropdown
                                        color="muted"
                                        items={getQuestionAlternativeMenuItems(v, index)}
                                        disabled={getQuestionAlternativeMenuItems(v, index).length == 0}
                                        onChange={(selection) => handleSelectAlternativeOption(selection, v, index)}
                                    />
                                }
                            </Grid>
                        </Grid>
                    );
                })
            }
            <Box mt={1} >
                {
                    readonly != true &&
                    <MprcButton
                        onClick={() => handleAddAlternative()}
                        disabled={viewData.alternatives.length >= MAX_ALTERNATIVES || viewData.changeVersion == false}
                    >
                        + {t("Question.Edit.FillIn.AddItemButtonText")}
                    </MprcButton>
                }
            </Box>
            {
                !!currentPostValidation.alternatives &&
                <FormHelperText error>{<MprcStringToHtml text={currentPostValidation?.alternatives} />}</FormHelperText>
            }

            {
                viewData.questionType == QuestionType.Dropdown &&
                <Box mt={2}>
                    <MprcFormSubHeader
                        title={t("Question.Edit.FillIn.OtherIncorrectAnswersLabel")}
                    />
                    <MprcChipInput
                        fullWidth
                        disabled={readonly}
                        placeholder={t("Question.Edit.FillIn.OtherIncorrectAnswersPlaceholder")}
                        selectedItems={viewData.evaluation?.otherIncorrectAnswers ?? []}
                        onChange={(items) => { handleUpdateEvaluation("otherIncorrectAnswers", items); }}
                    />
                </Box>
            }
        </>
    );
};
